import { useState, useMemo, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Avatar,
  CircularProgress,
  Pagination,
  Autocomplete,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useGetAllUsersQuery, useGetSavedLayoutByPartnerIdQuery, useGetLayoutsEngagementByPartnerQuery, useGetAllSavedLayoutsForAdminQuery, usePartnersQuery } from "../../Slices/roomNateApis";
import { truncateText } from "../../utils/truncateText";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useGlobalContext } from "../../context/GlobalContext";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { formatUptime } from "../../utils/format";
import CardDetails from "../CardDetails";
import EngagementLogs from "../EngagementLogs";

/** Normalize GET /savedLayout/all when the API returns a bare array (per-user admin) vs { layouts, totalResults, … }. */
function normalizeAllLayoutsResponse(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    const n = raw.length;
    return { layouts: raw, totalResults: n, limit: Math.max(n, 1) };
  }
  return raw;
}

const PartnersLayout  = ({ partnerId: partnerIdProp })  => {

    const { currentUserSessionStorage } = useGlobalAuthContext();
    const { activePartner: activePartnerCtx, setActivePartner } = useGlobalContext();
    // Single source of truth: global activePartner (sidebar + this page stay in sync).
    // Partner detail "Floor Plans" tab passes partnerId — scope is fixed to that partner.
    const partnerId = partnerIdProp ?? activePartnerCtx;
    const isAllPartners = !partnerId;
    const showPagePartnerPicker = partnerIdProp == null;

    const [page, setPage] = useState(1);
    const [user, setUser] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(dayjs());
    const [engagementLogsDrawerOpen, setEngagementLogsDrawerOpen] = useState(false);
    const [engaementLogsTab, setEngagementLogsTab] = useState(0);
    const [engagementLogsLayout, setEngagementLogsLayout] = useState(null);

     const {data: users } = useGetAllUsersQuery();

    const { data: partnersData } = usePartnersQuery("", {
      skip: !currentUserSessionStorage || !showPagePartnerPicker,
    });

    const handlePartnerScopeChange = (e) => {
      const id = e.target.value;
      setActivePartner(id);
      Cookies.set("activePartner", id, { expires: 30 });
    };

    const { data: layoutsByPartner, isFetching: fetchingLayoutsByPartner } = useGetSavedLayoutByPartnerIdQuery(
      { id: partnerId, page: page, user, startDate, endDate },
      { skip: isAllPartners }
    );
    const { data: layoutsAllRaw, isFetching: fetchingLayoutsAll } = useGetAllSavedLayoutsForAdminQuery(
      { page, user, startDate, endDate },
      { skip: !isAllPartners }
    );

    const layoutsData = useMemo(() => {
      if (isAllPartners) return normalizeAllLayoutsResponse(layoutsAllRaw);
      return layoutsByPartner;
    }, [isAllPartners, layoutsAllRaw, layoutsByPartner]);

    const fetchingLayout = isAllPartners ? fetchingLayoutsAll : fetchingLayoutsByPartner;

    const { data: engagementData, isFetching: fetchingEngagement } = useGetLayoutsEngagementByPartnerQuery({
      partnerId: isAllPartners ? "all" : partnerId,
      startDate,
      endDate,
      user,
    });

    useEffect(() => {
      setPage(1);
    }, [isAllPartners, partnerId]);

    function filterLayouts(data) {
        let result = [];
    
        if (data.image) {
          result.push(data);
        }
    
        if (data.children) {
          data.children.forEach((child) => {
            result = result.concat(filterLayouts(child));
          });
        }
    
        return result;
      }

    return (
        <Box
            sx={{
                    py: 2,
                    px: 2,
                    position: "relative",
                    zIndex: 0,
            }}
        >
            <Box sx={{ pt: 2 }}>
                <Typography variant="h6" component="h6" sx={{ mb: 0.5 }}>
                  Search Filter
                </Typography>
                {showPagePartnerPicker && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
                    Start with all partners to see full activity, then choose a partner below to filter
                    (same scope as the sidebar partner control — both stay in sync).
                  </Typography>
                )}
                <Grid container spacing={2} style={{ marginTop: "0px" }}>
                {showPagePartnerPicker && (
                  <Grid item xs={12}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id="floor-plans-partner-label">Partner</InputLabel>
                      <Select
                        labelId="floor-plans-partner-label"
                        value={activePartnerCtx ?? ""}
                        label="Partner"
                        onChange={handlePartnerScopeChange}
                        sx={{ borderRadius: "10px" }}
                      >
                        <MenuItem value="">
                          <em>All partners</em>
                        </MenuItem>
                        {partnersData?.map((p) => (
                          <MenuItem key={p._id} value={p._id}>
                            {p.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {users?.length > 0 && (
                    <>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box sx={{ ".MuiFormControl-root": { width: "100%" } }}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        onChange={(value) => setStartDate(value)}
                                        slotProps={{
                                            actionBar: { actions: ["clear"] },
                                        }}
                                        sx={{ width: "100%" }}
                                    />
                                </Box>
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Box sx={{ ".MuiFormControl-root": { width: "100%" } }}>
                                    <DatePicker
                                        label="End Date"
                                        minDate={startDate}
                                        value={endDate}
                                        onChange={(value) => setEndDate(value)}
                                        slotProps={{
                                            actionBar: { actions: ["clear"] },
                                        }}
                                        sx={{ width: "100%" }}
                                    />
                                </Box>
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                disablePortal
                                id="combo-box-demo"
                                options={users?.map((user) => ({label: user?.name || user?.email || "", value: user?._id})) || []}
                                onChange={(event, value) =>
                                    setUser(value?.value)
                                }
                                renderInput={(params) => (
                                    <TextField {...params} label="Select User" />
                                )}
                            />
                        </Grid>

                    </>
                )}
                </Grid>
            </Box>

          
            {
                engagementData && !fetchingEngagement && (
                    <>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "20px",
                        padding: "20px"
        
                    }}>
                        <CardDetails title="Total Views" value={engagementData?.metricCards?.totalGalleryViews + engagementData?.metricCards?.totalEditorViews} viewLogs={() => {
                            setEngagementLogsTab(0);
                            setEngagementLogsDrawerOpen(true);
                        }}/>
                        <CardDetails title="Floor Plans"  value={engagementData?.metricCards?.totalLayouts} />
                        <CardDetails title="Versions" value={engagementData?.metricCards?.totalVersions}/>
                    </Box>
            
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "20px",
                        padding: "20px"
                        
                    }}>
                        <CardDetails title="Downloads" value={engagementData?.metricCards?.totalDownloads} viewLogs={() => {
                            setEngagementLogsTab(1);
                            setEngagementLogsDrawerOpen(true);
                        }}/>
                        <CardDetails title="Shares" value={engagementData?.metricCards?.totalShares} viewLogs={() => {
                            setEngagementLogsTab(2);
                            setEngagementLogsDrawerOpen(true);
                        }}/>
                        <CardDetails title="Shuffles"  value={engagementData?.metricCards?.totalShuffles}/>
                        <CardDetails title="Time Spent" value={formatUptime(engagementData?.metricCards?.totalUptime)}/>
                    </Box>
                    </>
                )
            }

            {fetchingEngagement && !fetchingLayout && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 160,
                  width: "100%",
                  py: 3,
                }}
              >
                <CircularProgress size={22} />
                <Typography ml={1.5} color="text.secondary">
                  Loading metrics…
                </Typography>
              </Box>
            )}

            {fetchingLayout && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 220,
                  width: "100%",
                  py: 4,
                  color: "text.secondary",
                }}
              >
                <CircularProgress size={22} />
                <Typography ml={1.5}>Loading layouts…</Typography>
              </Box>
            )}
              {!fetchingLayout && ( 
                <>
                <Grid container spacing={2} mt={0}>
                {
                    layoutsData
                        ?.layouts
                        ?.flatMap(filterLayouts)
                        .filter((item) => item.image)?.length >= 1 ? (
                        layoutsData
                        ?.layouts
                        ?.flatMap(filterLayouts)
                        ?.filter(
                            (item) =>
                            item.image &&
                            item?.userSavedLayoutsCount > 0
                        )
                        ?.map((item, idx) => {
                            const childrenViews = (item?.copiedLayoutCumulativeGalleryViews + item?.copiedLayoutCumulativeEditorViews);
                            const childrenShares = (item?.copiedLayoutCumulativeShares);
                            const childrenDownloads = (item?.copiedLayoutCumulativeDownloads);
                            const childrenUptime = (item?.copiedLayoutCumulativeUptime);

                            const selfViews = (item?.totalGalleryViews + item?.totalEditorViews);
                            const selfShares = (item?.totalShares);
                            const selfDownloads = (item?.totalDownloads);
                            const selfUptime = (item?.totalUptime);

                            const cumulativeViews = childrenViews + selfViews;
                            const cumulativeShares = childrenShares + selfShares;
                            const cumulativeDownloads = childrenDownloads + selfDownloads;
                            const cumulativeUptime = childrenUptime + selfUptime;

                            
                            return (
                            <Grid item xs={6} sm={4}>
                                <Box
                                key={`${item?._id}-${idx}`}
                                sx={{
                                    border: "1px solid #EBEDF0",
                                    py: 2,
                                    px: 1.5,
                                    position: "relative",
                                    background: "#FFFFFF",
                                }}
                                onClick={() => {
                                    window.open(`/floor-gallery/${item?._id}`, '_blank');
                                }}
                                >
                                <Box
                                key={`${item?._id}-${idx}`}
                                sx={{
                                    border: "1px solid #EBEDF0",
                                    py: 2,
                                    px: 1.5,
                                    borderRadius: "8px",
                                    position: "relative",
                                    background: "#EBEDF080",
                                    cursor: "pointer",
                                }}
                                onClick={() => {
                                    window.open(`/floor-gallery/${item?._id}`, '_blank');
                                }}
                                >
                                <Box
                                    color="primary"
                                    sx={{
                                    position: "absolute",
                                    top: 8,
                                    left: 8,
                                    zIndex: 999,
                                    color: "white",
                                    padding: "3px 6px 3px 6px",
                                    backgroundColor: "#20212466",
                                    borderRadius: "4px !important",
                                    fontWeight: 600,
                                }}
                                >
                                    {item?.userSavedLayoutsCount || 1}{" "}Version(s)
                                </Box>



                                <Avatar
                                    src={
                                    item?.thumbnailfurnishedImageId ||
                                    item?.thumbnailImage ||
                                    item?.furnishedImageId ||
                                    item?.image
                                    }
                                    alt={item?.name || "Avatar"} 
                                    sx={{
                                    width: "100%",
                                    "& img": {
                                        width: "100%",
                                        objectFit: "contain ",
                                    },
                                    height: 115,
                                    borderRadius: 0,
                                    }}
                                />


                                <Box
                                    color="primary"
                                    sx={{
                                    position: "absolute",
                                    bottom: 8,
                                    left: 8,
                                    zIndex: 999,
                                    color: "white",
                                    padding: "3px 6px 3px 6px",
                                    backgroundColor: "#20212466",
                                    borderRadius: "4px !important",
                                    fontWeight: 600,
                                    }}
                                >
                                    {new Date(item?.createdAt)?.toLocaleDateString()|| ""}
                                </Box>
                                </Box>
                                 <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: 2 }}>
                                    <Box
                                        sx={{
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        fontWeight: 600,
                                        fontSize: "0.85rem",
                                        }}
                                    >
                                        <Typography
                                        sx={{
                                            fontSize: "14px",
                                            mt: 0.5,
                                            fontWeight: 800,
                                        }}
                                        >
                                        {item?.name ||
                                            truncateText(
                                            item?.payload?.prompt,
                                            19
                                            ) ||
                                            "Untitled"}
                                        </Typography>
                                        <Typography
                                        sx={{
                                            fontSize: "12px",
                                            mt: 0.5,
                                        }}
                                        >
                                        Uploaded By: <strong>{item?.user?.name || ""}</strong>
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'flex-end',
                                        minWidth: 130,
                                        padding: '4px 8px',
                                        ml: 2,
                                        }}
                                    >
                                        <span style={{ fontSize: '0.85rem', color: '#222' }}>Total Time Spent</span>
                                        <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{formatUptime(Number(cumulativeUptime || 0))}</span>
                                    </Box>
                                    </Box>

                                <Box
                                    sx={{
                                        width: '100%',
                                        padding: '4px 8px',
                                        fontWeight: 400,
                                        fontSize: '0.95rem',
                                        mt: 1
                                    }}
                                >
                                
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>Total</Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            borderRadius: '4px',
                                            fontWeight: 400,
                                            fontSize: '0.95rem',
                                            mt: 1
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 70 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Views</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{cumulativeViews ?? '-'}</span>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 130 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Avg. Time Spent per View</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{((cumulativeViews) > 0) ? formatUptime(Number((cumulativeUptime) / (cumulativeViews)).toFixed(2)) : '-'}</span>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Shares</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{cumulativeShares ?? '-'}</span>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Downloads</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{cumulativeDownloads ?? '-'}</span>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        width: '100%',
                                        padding: '4px 8px',
                                        fontWeight: 400,
                                        fontSize: '0.95rem',
                                        mt: 1
                                    }}
                                >
                                
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>Original</Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            borderRadius: '4px',
                                            fontWeight: 400,
                                            fontSize: '0.95rem',
                                            mt: 1
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 70 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Views</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{selfViews ?? '-'}</span>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 130 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Avg. Time Spent per View</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{((selfViews) > 0) ? formatUptime(Number((selfUptime) / (selfViews)).toFixed(2)) : '-'}</span>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Shares</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{selfShares ?? '-'}</span>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Downloads</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{selfDownloads ?? '-'}</span>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        width: '100%',
                                        padding: '4px 8px',
                                        fontWeight: 400,
                                        fontSize: '0.95rem',
                                        mt: 1
                                    }}
                                >
                                
                                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>Copies</Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            borderRadius: '4px',
                                            fontWeight: 400,
                                            fontSize: '0.95rem',
                                            mt: 1
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 70 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Views</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{Math.max((childrenViews), 0) ?? '-'}</span>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 130 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Avg. Time Spent per View</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{(((childrenViews)) > 0) ? formatUptime(Number(((childrenViews)) / ((childrenViews))).toFixed(2)) : '-'}</span>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Shares</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{Math.max((childrenShares ?? 0) , 0) ?? '-'}</span>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 60 }}>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Downloads</span>
                                                <span style={{ fontWeight: 900, fontSize: '1.1rem', color: 'black' }}>{Math.max((childrenDownloads ?? 0) , 0) ?? '-'}</span>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                                    <Box sx={{ padding: '4px 8px', cursor: 'pointer' }}>
                                        <span 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setEngagementLogsTab(0);
                                                setEngagementLogsDrawerOpen(true);
                                                setEngagementLogsLayout({
                                                    _id: item?._id,
                                                    name: item?.name,
                                                });
                                            }}
                                            style={{ fontSize: '0.85rem', color: 'black', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                                                See users who viewed
                                        </span>
                                    </Box>
                                    {
                                        item?.baseCopiedFrom && (
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'flex-start',
                                                minWidth: 100,
                                                paddingLeft: 2,
                                                marginLeft: 3,
                                                height: '100%',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => window.open(`/floor-gallery/${item?.baseCopiedFrom._id}`)}>
                                                <>
                                                <span style={{ fontSize: '0.85rem', color: '#222' }}>Copied From</span>
                                                <span style={{fontWeight: 900, fontSize: '1.1rem', color: 'black'  }}>{item?.baseCopiedFrom.name}</span>
                                                </>
                                            </Box>
                                        )
                                    }
                                </Box>

                               
                                </Box>
                            </Grid>
                            );
                        })
                    ) : (
                        <Grid item xs={12}>
                        <Typography>No layout found.</Typography>
                        </Grid>
                    )
                }
                </Grid>
                </>
            )}


            <br></br>
            {
                !fetchingLayout && layoutsData?.layouts?.length > 0 && (
                    <Pagination
                        count={Math.ceil(layoutsData?.totalResults/layoutsData?.limit)}
                        color="primary"
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        sx={{
                        ".MuiPagination-ul": {
                            justifyContent: "center",
                            width: "100%",
                        },
                        }}
                    />
                )
            }
        
          
            <Box sx={{ mt: 4 }}>
                <EngagementLogs
                    drawerOpen={engagementLogsDrawerOpen} 
                    onClose={() => {
                        setEngagementLogsDrawerOpen(false)
                        setEngagementLogsLayout(null)
                        setEngagementLogsTab(0)
                    }}
                    startDate={startDate}
                    endDate={endDate}
                    tab={engaementLogsTab}
                    layout={engagementLogsLayout}
                />
            </Box>
        </Box>
    );

}


export default PartnersLayout;